import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { useFinance, Field, inputCls, kicker, Loading, todayISO, fmt } from "@/admin/finance/financeUtils";

export default function FinanceSettings() {
  const { data, loading, reload } = useFinance("/finance/settings", []);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF(data); }, [data]);

  const equip = useFinance("/finance/equipment", []);

  if (loading || !f) return <Loading />;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/finance/settings", {
        vat_enabled: f.vat_enabled, vat_rate: Number(f.vat_rate), prices_include_vat: f.prices_include_vat,
        default_hourly_rate: Number(f.default_hourly_rate), thresholds: {
          high: Number(f.thresholds.high), healthy: Number(f.thresholds.healthy), low: Number(f.thresholds.low),
        },
      });
      toast.success("Settings saved"); reload();
    } catch (e) { console.error(e); toast.error("Could not save settings"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="finance-settings">
      <div className="ascendia-card p-5">
        <p className={kicker}>Tax / VAT</p>
        <p className="mt-1 text-xs text-muted-c">Configure your VAT status. This is for internal accounting views only — not a legal tax filing.</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <Field label="VAT applicable">
            <select className={inputCls} value={f.vat_enabled ? "yes" : "no"} onChange={(e) => setF({ ...f, vat_enabled: e.target.value === "yes" })} data-testid="set-vat-enabled">
              <option value="yes">Yes</option><option value="no">No</option>
            </select>
          </Field>
          <Field label="VAT rate (%)"><input type="number" className={inputCls} value={f.vat_rate} onChange={(e) => setF({ ...f, vat_rate: e.target.value })} data-testid="set-vat-rate" /></Field>
          <Field label="Prices">
            <select className={inputCls} value={f.prices_include_vat ? "incl" : "excl"} onChange={(e) => setF({ ...f, prices_include_vat: e.target.value === "incl" })}>
              <option value="incl">Include VAT</option><option value="excl">Exclude VAT</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="ascendia-card p-5">
        <p className={kicker}>Margin health thresholds (%)</p>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <Field label="High profit ≥"><input type="number" className={inputCls} value={f.thresholds.high} onChange={(e) => setF({ ...f, thresholds: { ...f.thresholds, high: e.target.value } })} /></Field>
          <Field label="Healthy ≥"><input type="number" className={inputCls} value={f.thresholds.healthy} onChange={(e) => setF({ ...f, thresholds: { ...f.thresholds, healthy: e.target.value } })} /></Field>
          <Field label="Low margin ≥"><input type="number" className={inputCls} value={f.thresholds.low} onChange={(e) => setF({ ...f, thresholds: { ...f.thresholds, low: e.target.value } })} /></Field>
        </div>
        <p className="mt-2 text-[11px] text-muted-c">Below the Low threshold (or negative) a job is flagged LOSS / LOW MARGIN.</p>
      </div>

      <div className="ascendia-card p-5">
        <p className={kicker}>Defaults</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Field label="Default worker hourly rate"><input type="number" className={inputCls} value={f.default_hourly_rate} onChange={(e) => setF({ ...f, default_hourly_rate: e.target.value })} /></Field>
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="finance-settings-save" className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save settings"}</button>

      {/* Equipment tracker */}
      <EquipmentPanel equip={equip} />
    </div>
  );
}

function EquipmentPanel({ equip }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const add = async () => {
    if (!name) { toast.error("Name required"); return; }
    try { await api.post("/finance/equipment", { name, purchase_price: Number(price) || 0, purchase_date: todayISO() }); setName(""); setPrice(""); equip.reload(); }
    catch (e) { console.error(e); toast.error("Could not add equipment"); }
  };
  const addEntry = async (id) => {
    const amt = prompt("Repair / maintenance cost (₪):");
    if (!amt) return;
    try { await api.post(`/finance/equipment/${id}/entry`, { type: "maintenance", amount: Number(amt) || 0, note: "" }); equip.reload(); }
    catch (e) { console.error(e); toast.error("Could not add cost"); }
  };
  const del = async (id) => { try { await api.delete(`/finance/equipment/${id}`); equip.reload(); } catch (e) { console.error(e); } };
  return (
    <div className="ascendia-card p-5" data-testid="finance-equipment">
      <p className={kicker}>Equipment</p>
      <div className="mt-3 flex gap-2">
        <input className={inputCls} placeholder="e.g. DeWALT vacuum" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="number" className={`${inputCls} w-36`} placeholder="Price" value={price} onChange={(e) => setPrice(e.target.value)} />
        <button onClick={add} className="flex-shrink-0 rounded-lg border border-[color:var(--border-strong)] px-3 text-gold-soft hover:bg-white/5"><Plus className="h-4 w-4" /></button>
      </div>
      <div className="mt-4 space-y-2">
        {(equip.data || []).map((e) => (
          <div key={e.id} className="flex items-center justify-between rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-sm">
            <div><span className="text-white">{e.name}</span> <span className="text-muted-c text-xs">— lifetime {fmt(e.lifetime_cost)}</span></div>
            <div className="flex gap-2"><button onClick={() => addEntry(e.id)} className="text-xs text-gold-soft hover:underline">+ cost</button><button onClick={() => del(e.id)} className="text-rose-300"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
        {equip.data && equip.data.length === 0 && <p className="text-xs text-muted-c">No equipment tracked yet.</p>}
      </div>
    </div>
  );
}
